import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import type { MenuOption } from "../Menu";
import { QuickActionsMenu } from "../QuickActionsMenu";

/**
 * A breadcrumb that names its own destination. Needed where the parent crumb is
 * not the route's own ancestor — the treatment/package/membership builders live
 * under /admin/platforms/:id/... but belong to the Services list.
 */
export type PageLayoutCrumb = { label: ReactNode; to: string };

const isLinkedCrumb = (crumb: ReactNode | PageLayoutCrumb): crumb is PageLayoutCrumb =>
  typeof crumb === "object" && crumb !== null && "to" in crumb;

export type PageLayoutProps = {
  crumbs: (ReactNode | PageLayoutCrumb)[];
  title?: ReactNode;
  description?: ReactNode;
  element?: ReactNode;
  quickMenuOptions?: MenuOption[];
  children: ReactNode;
};

export const PageLayout = ({
  crumbs,
  title,
  description,
  element,
  quickMenuOptions,
  children,
}: PageLayoutProps) => {
  return (
    <div
      className="container mx-auto h-full w-full bg-surface-muted px-4 py-6 sm:px-8"
      data-testid="PageLayout"
    >
      <div className="flex h-full w-full flex-col gap-4 pb-4">
        <div className="relative flex w-full flex-wrap items-center justify-between gap-6">
          {title ? (
            <div>
              <h1 className="type-heading-lg text-foreground">{title}</h1>
              {description && (
                <p className="mt-2 type-body-lg text-muted-foreground">{description}</p>
              )}
            </div>
          ) : (
            crumbs.length > 0 && <Crumbs crumbs={crumbs} />
          )}
          <div className="flex w-full items-center justify-between gap-4 sm:w-auto sm:gap-12">
            {element && <div>{element}</div>}
            <div className="fixed right-2 z-20 sm:right-0">
              <QuickActionsMenu options={quickMenuOptions} />
            </div>
          </div>
        </div>
        <div className="relative min-h-0 flex-1 pb-8">{children}</div>
      </div>
    </div>
  );
};

const Crumbs = ({ crumbs }: { crumbs: (ReactNode | PageLayoutCrumb)[] }) => {
  const { t } = useTranslation();
  const reversed = [...crumbs].reverse();

  const links = reversed.flatMap((crumb, index) => {
    const originalIndex = crumbs.length - 1 - index;

    return index === 0
      ? [
          <span className="text-foreground" key={originalIndex}>
            {isLinkedCrumb(crumb) ? crumb.label : crumb}
          </span>,
        ]
      : [
          <span aria-hidden="true" key={`separator-${originalIndex}`}>
            /
          </span>,
          <Link
            className="text-link"
            key={originalIndex}
            to={isLinkedCrumb(crumb) ? crumb.to : "../".repeat(index)}
          >
            {isLinkedCrumb(crumb) ? crumb.label : crumb}
          </Link>,
        ];
  });

  return (
    <nav
      aria-label={t("common.breadcrumb")}
      className="flex flex-wrap items-center gap-2 type-body-md text-muted-foreground sm:gap-4 sm:type-heading-md"
    >
      {[...links].reverse()}
    </nav>
  );
};
